/*
Copyright (c) 2012-2023 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable no-return-assign */
/* eslint-disable prefer-template */
/* eslint-disable no-plusplus */
/* eslint-disable no-underscore-dangle */
/* eslint-disable lines-between-class-members */
import { Logger } from 'lib/logging';
import { ConsoleLogger } from 'lib/logging/default';
import { configWithRouteName, redactHermesEvent } from './helpers';
import {
  SDKConfig,
  Subscription,
  Unsubscribe,
  Callback,
  HermesEvent,
  Topic,
  ErrorTarget,
  HermesError,
  LogOperationNames,
  OPERATION_KEY,
} from './types';

export default class SDK {
  static readonly LOG_COMPONENT_NAME = "HermesWebSDK";

  private static _hermesVersion = 2;

  private _httpClient: SDKConfig['httpClient'];

  private _logger: Logger;

  private _webSocketURL: string;

  private _pingIntervalInMs: number;

  private _pingIntervalID: number | undefined;

  private _subscriptionIDSeed: number;

  private _subscriptions: Map<number, Subscription>;

  private readonly _maxReconnectAttempts: number;

  private _reconnectAttempts = 0;

  private _forcedDisconnect = false;

  // Only store a single `_socket` object because the SDK is designed to
  // support one connection for a single user only
  private _socket: WebSocket | undefined;

  constructor(config: SDKConfig) {
    this._httpClient = config.httpClient;
    this._logger = config.logger ?? new ConsoleLogger();
    this._webSocketURL = config.webSocketURL;
    this._pingIntervalInMs = config.pingIntervalInMs || 30000;
    this._maxReconnectAttempts = config.maxReconnectAttempts || 3;
    this._subscriptionIDSeed = 0;
    this._subscriptions = new Map();
  }

  // If there are no `topics`, the client would be subscribed to all topics.
  // Unsubscribe when the subscription is no longer needed to prevent memory
  // leaks.
  subscribe = (callback: Callback, topics: Topic[] = []): Unsubscribe => {
    const id = this._issueSubscriptionID();
    this._subscriptions.set(id, {
      callback,
      topics,
    });
    return () => {
      this._subscriptions.delete(id);
    };
  }

  clearSubscriptions = () => {
    this._subscriptions.clear();
  }

  authenticate = async (refuseConnectionIfExists = true) => {
    try {
      await this._httpClient.post('', undefined, configWithRouteName("/ws")({ // Base URL should have the /ws path already
        params: {
          rcie: refuseConnectionIfExists,
        }
      }));
    } catch (err) {
      this._logError(LogOperationNames.WSAuthenticate, "Error", { error: err });

      // We could check `isAxiosError` in case err is not an AxiosError, but
      // it does not make a difference if we can safely read the status code
      const e = err;
      const code = e?.response?.status;
      throw new HermesError(
        `Authentication failed. Resp status: ${code}`,
        ErrorTarget.Authenticate,
        code,
      );
    }
  }

  connect = (refuseConnectionIfExists = true, isReconnect = false) => {
    // Why prevent a connection if we are not disconnected?
    //
    // If the socket is already connecting or connected, there is no point in
    // connecting again. If the client wants a new connection, it should
    // disconnect from the existing one first.
    //
    // If we do not have this check, then it is possible to create multiple
    // sockets and it may be ambiguous which one will "win" (race condition).
    // It is possible that the latest `this._socket` may not be the "winner".
    //
    // If the socket is disconnecting, we should prevent a new socket from
    // being created for these reasons:
    // (1) The backend may still think the connection is active, so it
    // the new socket creation may fail if `rcie` is true.
    // (2) If we do not have this check, then in a race condition, the SDK may
    // get an `onopen` (new socket) followed by `onclose` (old socket) which is
    // confusing.
    //
    // To be clear, this frontend constraint is useful regardless of the value
    // of `rcie`. The `rcie` param is more of a backend constraint.
    if (!this.isDisconnected()) {
      throw new Error(
        'Cannot connect as the current connection is not disconnected'
      );
    }

    if (isReconnect) {
      this._reconnectAttempts++;
      console.info("Reconnecting attempt count: " + this._reconnectAttempts);
      this._logInfo(LogOperationNames.WSConnect, `Reconnecting attempt=${this._reconnectAttempts}`);
      if (this._reconnectAttempts > this._maxReconnectAttempts) {
        console.info("Max reconnecting attempts reached");
        this._notifySubscribers({
          topic: Topic.HermesWebSDKReconnectionFailure,
        });
        this._logError(LogOperationNames.WSConnect, `Reconnect failure`);
        return;
      }
    } else {
      this._logInfo(LogOperationNames.WSConnect);
      this._reconnectAttempts = 0;
      this._forcedDisconnect = false;
    }

    this._socket = new WebSocket(`${this._webSocketURL}?rcie=${refuseConnectionIfExists}`);

    this._notifySubscribers({
      topic: Topic.HermesWebSDKConnecting,
    });

    this._socket.onopen = this._onSocketOpen;
    this._socket.onmessage = this._onSocketMessage;
    this._socket.onerror = this._onSocketError;
    this._socket.onclose = this._onSocketClose;
  }

  disconnect = () => {
    if (this.isDisconnecting()) {
      throw new Error(
        'Cannot disconnect as the current connection is disconnecting'
      );
    }
    if (this.isDisconnected()) {
      throw new Error(
        'Cannot disconnect as the current connection is disconnected'
      );
    }

    this._forcedDisconnect = true;
    this._logInfo(LogOperationNames.WSClose);
    this._socket?.close(); // TODO: need more specific params?

    this._notifySubscribers({
      topic: Topic.HermesWebSDKDisconnecting,
    });
  }

  isConnecting = () => this._socket?.readyState === WebSocket.CONNECTING;

  isConnected = () => this._socket?.readyState === WebSocket.OPEN;

  isDisconnecting = () => this._socket?.readyState === WebSocket.CLOSING;

  isDisconnected = () => (
    !this._socket ||
    this._socket.readyState === WebSocket.CLOSED
  );

  send = (event: HermesEvent) => {
    if (!this.isConnected()) {
      throw new Error('Cannot send as the connection is not connected');
    }

    this._logInfo(LogOperationNames.WSSend, `topic=${event.topic}`, { hermes_event: redactHermesEvent(event) });

    this._socket?.send(JSON.stringify({
      ...event,
      version: SDK._hermesVersion,
    }));
  }

  private _onSocketOpen = (event: Event) => {
    console.log('_onSocketOpen', event);

    this._logInfo(LogOperationNames.WSOpen, "");

    this._notifySubscribers({
      topic: Topic.HermesWebSDKConnected,
    });
    this._setupPings();
  }

  private _setupPings = () => {
    if (this._pingIntervalID) {
      clearInterval(this._pingIntervalID);
    }

    this._pingIntervalID = window.setInterval(() => {
      if (this.isConnected()) {
        this.send({
          topic: Topic.HermesPing,
          payload: 'ping',
        });
      } else {
        clearInterval(this._pingIntervalID);
      }
    }, this._pingIntervalInMs);
  }

  private _onSocketMessage = (event: MessageEvent) => {
    console.log('_onSocketMessage', event);
    const hermesEvent: HermesEvent = JSON.parse(event.data);
    // TODO: Have some questions about previous hermes.ts SDK - do we need to
    // handle any event internally in the SDK?

    this._logInfo(LogOperationNames.WSReceive, `topic=${hermesEvent.topic}`, { hermes_event: redactHermesEvent(hermesEvent) });

    this._notifySubscribers(hermesEvent);
  }

  private _onSocketError = (event: Event) => {
    // TODO: Is the event payload useful for clients?
    console.error('_onSocketError', event);

    this._logError(LogOperationNames.WSError, "Error");

    this._notifySubscribers({
      topic: Topic.HermesWebSDKConnectionError,
    });
  }

  // It is possible for _onSocketClose to be called without a previous
  // _onSocketOpen, so do not assume that _onSocketOpen had ran earlier
  private _onSocketClose = (event: CloseEvent) => {
    // TODO: Is the event payload useful for clients?
    console.log('_onSocketClose', event);

    this._logInfo(LogOperationNames.WSClose, `code=${event.code}`);

    this._notifySubscribers({
      topic: Topic.HermesWebSDKDisconnected,
    });
    
    if (!this._forcedDisconnect && this.isDisconnected()) {
      this.connect(true, true);
    }
  }

  private _issueSubscriptionID = () => this._subscriptionIDSeed += 1;

  private _notifySubscribers = (event: HermesEvent) => {
    this._subscriptions.forEach(subscription => {
      const topics = subscription.topics;
      if (topics.length === 0 || topics.includes(event.topic)) {
        try {
          subscription.callback(event);
        } catch (err) {
           this._logError(LogOperationNames.NotifySubscriber, "Error");
        }
      }
    });
  }

  // TODO: Detect connection stability issues early (disconnection may be
  // delayed, x number of missed pongs could be better signal)

  private _logInfo = (operationName: string, message?: string, info?: object) => {
    this._logger.info(`${operationName} ${message || ""}`, {
      component: SDK.LOG_COMPONENT_NAME,
      [OPERATION_KEY]: operationName,
      ...info,
    });
  }

  private _logError = (operationName: string, message?: string, info?: object) => {
    this._logger.error(`${operationName} ${message || ""}`, {
      component: SDK.LOG_COMPONENT_NAME,
      [OPERATION_KEY]: operationName,
      ...info,
    });
  }
}
